import { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';
import { LanguageCode } from '@/data/languages';

export type TranslationMode = 'manual' | 'ai';

export interface AddLanguageRequest {
  targetLanguage: LanguageCode;
  sourceLanguage: LanguageCode;
  translationMode: TranslationMode;
}

interface LanguageContextType {
  selectedLanguage: LanguageCode;
  setSelectedLanguage: (lang: LanguageCode) => void;
  availableLanguages: LanguageCode[];
  addLanguage: (lang: LanguageCode) => void;
  addLanguageWithTranslation: (request: AddLanguageRequest) => Promise<void>;
  removeLanguage: (lang: LanguageCode) => Promise<void>;
  setAvailableLanguages: (langs: LanguageCode[]) => void;
  isTranslating: boolean;
  isRemoving: boolean;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

interface LanguageProviderProps {
  children: ReactNode;
  availableLanguages?: LanguageCode[];
  onLanguagesChange?: (languages: LanguageCode[]) => void;
  onAddLanguageWithTranslation?: (request: AddLanguageRequest) => Promise<void>;
  onRemoveLanguage?: (lang: LanguageCode) => Promise<void>;
}

export function LanguageProvider({
  children,
  availableLanguages: initialLanguages = ['en'],
  onLanguagesChange,
  onAddLanguageWithTranslation,
  onRemoveLanguage,
}: LanguageProviderProps) {
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>(initialLanguages[0] || 'en');
  const [availableLanguages, setAvailableLanguagesState] = useState<LanguageCode[]>(initialLanguages);
  const [isTranslating, setIsTranslating] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  // Sync available languages when prop changes (e.g., when config loads)
  useEffect(() => {
    setAvailableLanguagesState(initialLanguages);
    // If current selection is not in the new list, select first available
    if (!initialLanguages.includes(selectedLanguage) && initialLanguages.length > 0) {
      setSelectedLanguage(initialLanguages[0]);
    }
  }, [initialLanguages.join(',')]);

  const addLanguage = useCallback(
    (lang: LanguageCode) => {
      if (!availableLanguages.includes(lang)) {
        const newLanguages = [...availableLanguages, lang];
        setAvailableLanguagesState(newLanguages);
        setSelectedLanguage(lang); // Auto-select the newly added language
        onLanguagesChange?.(newLanguages);
      }
    },
    [availableLanguages, onLanguagesChange],
  );

  const addLanguageWithTranslation = useCallback(
    async (request: AddLanguageRequest) => {
      if (availableLanguages.includes(request.targetLanguage)) {
        return; // Language already exists
      }

      // Both manual and AI modes go through the callback
      // ConfigEditor handles the actual backend requests
      if (onAddLanguageWithTranslation) {
        setIsTranslating(true);
        try {
          await onAddLanguageWithTranslation(request);
          // The callback will handle updating config and languages
        } finally {
          setIsTranslating(false);
        }
      } else {
        // Fallback: just add the language locally (no backend)
        addLanguage(request.targetLanguage);
      }
    },
    [availableLanguages, addLanguage, onAddLanguageWithTranslation],
  );

  const removeLanguage = useCallback(
    async (lang: LanguageCode) => {
      if (!availableLanguages.includes(lang)) {
        return; // Language doesn't exist
      }

      if (availableLanguages.length <= 1) {
        return; // Can't remove last language
      }

      if (onRemoveLanguage) {
        setIsRemoving(true);
        try {
          await onRemoveLanguage(lang);
          // The callback will handle updating config and languages
        } finally {
          setIsRemoving(false);
        }
      } else {
        // Fallback: just remove from local state
        const newLanguages = availableLanguages.filter((l) => l !== lang);
        setAvailableLanguagesState(newLanguages);
        onLanguagesChange?.(newLanguages);
        // If removed language was selected, switch to first available
        if (selectedLanguage === lang && newLanguages.length > 0) {
          setSelectedLanguage(newLanguages[0]);
        }
      }
    },
    [availableLanguages, selectedLanguage, onRemoveLanguage, onLanguagesChange],
  );

  const setAvailableLanguages = useCallback(
    (langs: LanguageCode[]) => {
      setAvailableLanguagesState(langs);
      onLanguagesChange?.(langs);
      // If current selected language is not in new list, select first available
      if (!langs.includes(selectedLanguage) && langs.length > 0) {
        setSelectedLanguage(langs[0]);
      }
    },
    [selectedLanguage, onLanguagesChange],
  );

  return (
    <LanguageContext.Provider
      value={{
        selectedLanguage,
        setSelectedLanguage,
        availableLanguages,
        addLanguage,
        addLanguageWithTranslation,
        removeLanguage,
        setAvailableLanguages,
        isTranslating,
        isRemoving,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export function useLanguageSafe() {
  return useContext(LanguageContext);
}
